import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Save, Bot } from 'lucide-react';
import { useAIModelConfig, AIModelConfiguration } from '@/hooks/useAIModelConfig';

const AVAILABLE_MODELS = [
  { value: 'gpt-5', label: 'GPT-5', description: 'Next generation model' },
  { value: 'gpt-4o', label: 'GPT-4o', description: 'Most capable model' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'Faster and cheaper' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', description: 'High performance' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', description: 'Fast and efficient' },
];

// Define which parameters are supported by each model
const MODEL_SUPPORTED_PARAMS: Record<string, {
  temperature: boolean;
  max_tokens: boolean;
  top_p: boolean;
  frequency_penalty: boolean;
  presence_penalty: boolean;
}> = {
  'gpt-5': {
    temperature: false,
    max_tokens: false,
    top_p: false,
    frequency_penalty: false,
    presence_penalty: false,
  },
  'gpt-4o': {
    temperature: true,
    max_tokens: true,
    top_p: true,
    frequency_penalty: true,
    presence_penalty: true,
  },
  'gpt-4o-mini': {
    temperature: true,
    max_tokens: true,
    top_p: true,
    frequency_penalty: true,
    presence_penalty: true,
  },
  'gpt-4-turbo': {
    temperature: true,
    max_tokens: true,
    top_p: true,
    frequency_penalty: true,
    presence_penalty: true,
  },
  'gpt-3.5-turbo': {
    temperature: true,
    max_tokens: true,
    top_p: true,
    frequency_penalty: true,
    presence_penalty: true,
  },
};

const AIModelConfigCard = () => {
  const { configuration, isLoading, error, updateConfiguration } = useAIModelConfig();
  const [localConfig, setLocalConfig] = useState<Partial<AIModelConfiguration>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (configuration) {
      const modelName = configuration.model_name || 'gpt-4o-mini';
      const supportedParams = MODEL_SUPPORTED_PARAMS[modelName] || MODEL_SUPPORTED_PARAMS['gpt-4o'];
      
      setLocalConfig({
        model_name: configuration.model_name,
        temperature: supportedParams.temperature ? (configuration.temperature ?? 0.7) : null,
        max_tokens: supportedParams.max_tokens ? (configuration.max_tokens ?? 800) : null,
        top_p: supportedParams.top_p ? (configuration.top_p ?? 1.0) : null,
        frequency_penalty: supportedParams.frequency_penalty ? (configuration.frequency_penalty ?? 0.0) : null,
        presence_penalty: supportedParams.presence_penalty ? (configuration.presence_penalty ?? 0.0) : null,
      });
    }
  }, [configuration]);

  const handleConfigChange = (field: keyof AIModelConfiguration, value: any) => {
    let newConfig = { ...localConfig, [field]: value };
    
    // If changing model, only set unsupported params to null, keep supported ones with their current values
    if (field === 'model_name') {
      const supportedParams = MODEL_SUPPORTED_PARAMS[value] || MODEL_SUPPORTED_PARAMS['gpt-4o'];
      
      // Only set to null if the parameter is not supported by the new model
      if (!supportedParams.temperature) {
        newConfig.temperature = null as any;
      }
      if (!supportedParams.max_tokens) {
        newConfig.max_tokens = null as any;
      }
      if (!supportedParams.top_p) {
        newConfig.top_p = null as any;
      }
      if (!supportedParams.frequency_penalty) {
        newConfig.frequency_penalty = null as any;
      }
      if (!supportedParams.presence_penalty) {
        newConfig.presence_penalty = null as any;
      }
      
      // For supported parameters, set default values if they don't exist
      if (supportedParams.temperature && (newConfig.temperature === null || newConfig.temperature === undefined)) {
        newConfig.temperature = 0.7;
      }
      if (supportedParams.max_tokens && (newConfig.max_tokens === null || newConfig.max_tokens === undefined)) {
        newConfig.max_tokens = 800;
      }
      if (supportedParams.top_p && (newConfig.top_p === null || newConfig.top_p === undefined)) {
        newConfig.top_p = 1.0;
      }
      if (supportedParams.frequency_penalty && (newConfig.frequency_penalty === null || newConfig.frequency_penalty === undefined)) {
        newConfig.frequency_penalty = 0.0;
      }
      if (supportedParams.presence_penalty && (newConfig.presence_penalty === null || newConfig.presence_penalty === undefined)) {
        newConfig.presence_penalty = 0.0;
      }
    }
    
    setLocalConfig(newConfig);
    
    // Check if there are changes
    if (configuration) {
      const hasChanges = Object.keys(newConfig).some(key => {
        const configKey = key as keyof AIModelConfiguration;
        return newConfig[configKey] !== configuration[configKey];
      });
      setHasChanges(hasChanges);
    }
  };
  
  // Get supported params for current model
  const currentModel = localConfig.model_name || 'gpt-4o-mini';
  const supportedParams = MODEL_SUPPORTED_PARAMS[currentModel] || MODEL_SUPPORTED_PARAMS['gpt-4o'];

  const handleSave = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    try {
      await updateConfiguration(localConfig);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save AI model configuration:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !configuration) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI Model Configuration
          </CardTitle>
          <CardDescription>
            Configure the AI model settings for content generation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          AI Model Configuration
        </CardTitle>
        <CardDescription>
          Configure the AI model settings for content generation. These settings will be used across all AI-powered features.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Model Selection */}
        <div className="space-y-2">
          <Label htmlFor="model">AI Model</Label>
          <Select
            value={localConfig.model_name || 'gpt-4o-mini'}
            onValueChange={(value) => handleConfigChange('model_name', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select AI model" />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_MODELS.map((model) => (
                <SelectItem key={model.value} value={model.value}>
                  <div className="flex flex-col">
                    <span>{model.label}</span>
                    <span className="text-xs text-muted-foreground">{model.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Max Tokens - only show if supported */}
        {supportedParams.max_tokens && (
          <div className="space-y-2">
            <Label htmlFor="max_tokens">Max Tokens</Label>
            <Input
              id="max_tokens"
              type="number"
              min="1"
              max="4096"
              value={localConfig.max_tokens || 800}
              onChange={(e) => handleConfigChange('max_tokens', parseInt(e.target.value) || 800)}
            />
            <p className="text-xs text-muted-foreground">
              Maximum number of tokens to generate (1-4096)
            </p>
          </div>
        )}

        {/* Temperature - only show if supported */}
        {supportedParams.temperature && (
          <div className="space-y-2">
            <Label htmlFor="temperature">Temperature: {localConfig.temperature || 0.7}</Label>
            <Slider
              id="temperature"
              min={0}
              max={2}
              step={0.1}
              value={[localConfig.temperature || 0.7]}
              onValueChange={([value]) => handleConfigChange('temperature', value)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Controls randomness: 0 = deterministic, 2 = very random
            </p>
          </div>
        )}

        {/* Top P */}
        {supportedParams.top_p && (
          <div className="space-y-2">
            <Label htmlFor="top_p">Top P: {localConfig.top_p || 1.0}</Label>
            <Slider
              id="top_p"
              min={0}
              max={1}
              step={0.1}
              value={[localConfig.top_p || 1.0]}
              onValueChange={([value]) => handleConfigChange('top_p', value)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Controls diversity: 0 = most likely tokens, 1 = all tokens
            </p>
          </div>
        )}

        {(supportedParams.frequency_penalty || supportedParams.presence_penalty) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Frequency Penalty */}
            {supportedParams.frequency_penalty && (
              <div className="space-y-2">
                <Label htmlFor="frequency_penalty">Frequency Penalty: {localConfig.frequency_penalty || 0.0}</Label>
                <Slider
                  id="frequency_penalty"
                  min={-2}
                  max={2}
                  step={0.1}
                  value={[localConfig.frequency_penalty || 0.0]}
                  onValueChange={([value]) => handleConfigChange('frequency_penalty', value)}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Reduces likelihood of repeating the same line
                </p>
              </div>
            )}

            {/* Presence Penalty */}
            {supportedParams.presence_penalty && (
              <div className="space-y-2">
                <Label htmlFor="presence_penalty">Presence Penalty: {localConfig.presence_penalty || 0.0}</Label>
                <Slider
                  id="presence_penalty"
                  min={-2}
                  max={2}
                  step={0.1}
                  value={[localConfig.presence_penalty || 0.0]}
                  onValueChange={([value]) => handleConfigChange('presence_penalty', value)}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Reduces likelihood of talking about new topics
                </p>
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        {hasChanges && (
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIModelConfigCard;
